import React, { useState, useRef, useEffect } from "react";
import "./MindMap.css";

const MindMap = ({ data, title, description }) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  if (!data || !data.content) {
    return (
      <div className="mindmap-container">
        <div className="mindmap-error">
          <h3>No Mind Map Data Available</h3>
          <p>Please generate a mind map first.</p>
        </div>
      </div>
    );
  }

  const { centralTopic, branches, metadata } = data.content;

  // Calculate node positions
  const calculateNodePositions = () => {
    const positions = {};
    const centerX = 400;
    const centerY = 300;
    const branchRadius = 180;
    const subBranchRadius = 100;

    // Central node
    positions["central"] = { x: centerX, y: centerY, level: 0 };

    // Branch nodes - spread them more evenly
    branches.forEach((branch, branchIndex) => {
      const angle = (branchIndex * 2 * Math.PI) / branches.length - Math.PI / 2; // Start from top
      const x = centerX + Math.cos(angle) * branchRadius;
      const y = centerY + Math.sin(angle) * branchRadius;
      positions[branch.id] = { x, y, level: 1 };

      // Sub-branch nodes - position them more naturally
      branch.children.forEach((subBranch, subIndex) => {
        const subAngle =
          angle + (subIndex - (branch.children.length - 1) / 2) * 0.4;
        const subX = x + Math.cos(subAngle) * subBranchRadius;
        const subY = y + Math.sin(subAngle) * subBranchRadius;
        positions[subBranch.id] = { x: subX, y: subY, level: 2 };
      });
    });

    return positions;
  };

  const positions = calculateNodePositions();

  // Handle mouse events for panning
  const handleMouseDown = (e) => {
    if (e.target.tagName === "svg" || e.target.tagName === "g") {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prev) => Math.max(0.3, Math.min(3, prev * delta)));
  };

  // Reset view
  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // Render node
  const renderNode = (nodeId, nodeData, isCentral = false) => {
    const pos = positions[nodeId];
    if (!pos) return null;

    const nodeStyle = {
      transform: `translate(${pos.x}px, ${pos.y}px)`,
      cursor: "pointer",
    };

    const nodeClass = `mindmap-node ${
      isCentral ? "central-node" : `level-${pos.level}`
    }`;
    const radius = isCentral ? 45 : pos.level === 1 ? 35 : 25;
    const fontSize = isCentral ? "13" : pos.level === 1 ? "11" : "9";

    // Split long text into multiple lines
    const maxCharsPerLine = isCentral ? 12 : pos.level === 1 ? 15 : 18;
    const words = nodeData.label.split(" ");
    const lines = [];
    let currentLine = "";

    words.forEach((word) => {
      if ((currentLine + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? " " : "") + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return (
      <g key={nodeId} style={nodeStyle}>
        <circle
          r={radius}
          className={nodeClass}
          onClick={() => setSelectedNode(nodeData)}
        />
        {lines.map((line, index) => (
          <text
            key={index}
            x={0}
            y={
              index * (parseInt(fontSize) + 2) -
              ((lines.length - 1) * (parseInt(fontSize) + 2)) / 2
            }
            textAnchor="middle"
            className="mindmap-text"
            fontSize={fontSize}
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  // Render connection line
  const renderConnection = (fromId, toId) => {
    const fromPos = positions[fromId];
    const toPos = positions[toId];
    if (!fromPos || !toPos) return null;

    return (
      <line
        key={`${fromId}-${toId}`}
        x1={fromPos.x}
        y1={fromPos.y}
        x2={toPos.x}
        y2={toPos.y}
        className="mindmap-connection"
        strokeWidth="2"
      />
    );
  };

  return (
    <div className="mindmap-container">
      <div className="mindmap-header">
        <div className="mindmap-title-section">
          <h3 className="mindmap-title">{title || "Mind Map"}</h3>
          <p className="mindmap-description">{description}</p>
        </div>
      </div>

      <div className="mindmap-content">
        <div className="mindmap-canvas-container">
          <svg
            ref={svgRef}
            width="100%"
            height="600"
            viewBox="0 0 800 600"
            className="mindmap-svg"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              transformOrigin: "center",
            }}
          >
            {/* Render connections first (so they appear behind nodes) */}
            {branches.map((branch) => {
              const connections = [];
              connections.push(renderConnection("central", branch.id));
              branch.children.forEach((subBranch) => {
                connections.push(renderConnection(branch.id, subBranch.id));
              });
              return connections;
            })}

            {/* Render nodes */}
            {renderNode("central", { label: centralTopic }, true)}
            {branches.map((branch) => {
              const nodes = [renderNode(branch.id, branch)];
              branch.children.forEach((subBranch) => {
                nodes.push(renderNode(subBranch.id, subBranch));
              });
              return nodes;
            })}
          </svg>
        </div>

        {/* Node details panel */}
        {selectedNode && (
          <div className="mindmap-details-panel">
            <div className="mindmap-details-header">
              <h4>{selectedNode.label}</h4>
              <button
                className="close-btn text-green"
                onClick={() => setSelectedNode(null)}
              >
                ✕
              </button>
            </div>
            <div className="mindmap-details-content">
              <p>
                <strong>Level:</strong> {selectedNode.level + 1}
              </p>
              {selectedNode.children && selectedNode.children.length > 0 && (
                <div>
                  <strong>Sub-topics:</strong>
                  <ul>
                    {selectedNode.children.map((child) => (
                      <li key={child.id}>{child.label}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="mindmap-controls">
          <button
            className="mindmap-control-btn"
            onClick={resetView}
            title="Reset View"
          >
            🔄
          </button>
          <div className="zoom-controls">
            <button
              className="mindmap-control-btn"
              onClick={() => setZoom((prev) => Math.max(0.3, prev - 0.1))}
              title="Zoom Out"
            >
              ➖
            </button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button
              className="mindmap-control-btn"
              onClick={() => setZoom((prev) => Math.min(3, prev + 0.1))}
              title="Zoom In"
            >
              ➕
            </button>
          </div>
        </div>
      </div>
      {/* Metadata info */}
      <div className="mindmap-metadata">
        <div className="metadata-item">
          <span className="metadata-label">Total Nodes:</span>
          <span className="metadata-value">{metadata?.totalNodes || 0}</span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Max Depth:</span>
          <span className="metadata-value">{metadata?.maxDepth || 0}</span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Difficulty:</span>
          <span className="metadata-value">
            {metadata?.difficulty || "N/A"}
          </span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Category:</span>
          <span className="metadata-value">{metadata?.category || "N/A"}</span>
        </div>
      </div>
    </div>
  );
};

export default MindMap;
